/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';
import { WarningBlock } from './WarningBlock';
import {
  generateTicks,
  getTimelineX,
  getTimeFromX,
  getTimelineTickHours,
} from './warningsTimelineUtils';
import type { TickType, TimelineLayout } from './warningsTimelineUtils';
import type { Warning } from '../../store/warningsForm/types';
import type { WarningListItemActions } from '../PublicWarningList/WarningListItemMenu';

export interface PositionedTick {
  time: Date;
  type: TickType;
  x: number;
}

export interface WarningLayout {
  key: string;
  left: number;
  top: number;
  warning: Warning;
  width: number;
}

interface TimelineBackgroundProps {
  contentWidth: number;
  layout: TimelineLayout;
  visibleHours: number;
  scrollContainer: HTMLDivElement | null;
  timelineEnd: Date;
  timelineStart: Date;
}

interface TimelineTimeBarProps extends TimelineBackgroundProps {
  fullWidth: number;
}

interface WarningBlocksLayerProps {
  hoveredWarningId?: string;
  onHoverWarning?: (warningId?: string) => void;
  onSelectWarning?: (warning: Warning) => void;
  selectedWarningId?: string;
  warningListItemActions?: WarningListItemActions;
  warningBlocks: WarningLayout[];
}

// A buffer of ticks outside the viewport
const TICK_OVERSCAN_PX = 240;

const getVisibleTicks = (
  scrollLeft: number,
  viewportWidth: number,
  timelineStart: Date,
  timelineEnd: Date,
  contentWidth: number,
): PositionedTick[] => {
  const tickWindowStartX = Math.max(0, scrollLeft - TICK_OVERSCAN_PX);
  const tickWindowEndX = Math.min(
    contentWidth,
    scrollLeft + viewportWidth + TICK_OVERSCAN_PX,
  );

  const tickWindowStart = getTimeFromX(
    tickWindowStartX,
    timelineStart,
    timelineEnd,
    contentWidth,
    0,
  );
  const tickWindowEnd = getTimeFromX(
    tickWindowEndX,
    timelineStart,
    timelineEnd,
    contentWidth,
    0,
  );

  const pixelsPerHour =
    contentWidth /
    ((timelineEnd.getTime() - timelineStart.getTime()) / 3600000);
  return generateTicks(
    tickWindowStart,
    tickWindowEnd,
    getTimelineTickHours(pixelsPerHour),
  ).map((tick) => ({
    ...tick,
    x: getTimelineX(tick.time, timelineStart, timelineEnd, contentWidth, 0),
  }));
};

const formatPeriodDate = (date: Date): string =>
  `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;

const getPeriodStarts = (
  ticks: PositionedTick[],
  timelineStart: Date,
  visibleHours: number,
): Date[] => {
  const current = new Date(ticks[0]?.time ?? timelineStart);
  const end = ticks[ticks.length - 1]?.time ?? timelineStart;
  current.setUTCHours(0, 0, 0, 0);
  const weekly = visibleHours >= 336;
  if (weekly) {
    current.setUTCDate(current.getUTCDate() - ((current.getUTCDay() + 6) % 7));
  }
  const starts: Date[] = [];
  while (current <= end) {
    starts.push(new Date(current));
    current.setUTCDate(current.getUTCDate() + (weekly ? 7 : 1));
  }
  return starts;
};

const hasSameTicks = (
  currentTicks: PositionedTick[],
  nextTicks: PositionedTick[],
): boolean => {
  return (
    currentTicks.length === nextTicks.length &&
    currentTicks.every((tick, index) => {
      const nextTick = nextTicks[index];
      return (
        tick.x === nextTick.x &&
        tick.type === nextTick.type &&
        tick.time.getTime() === nextTick.time.getTime()
      );
    })
  );
};

// Share frame-limited viewport updates between the background and the axis.
const useVisibleTicks = (
  scrollContainer: HTMLDivElement | null,
  contentWidth: number,
  timelineStart: Date,
  timelineEnd: Date,
): PositionedTick[] => {
  const [ticks, setTicks] = React.useState<PositionedTick[]>([]);

  React.useLayoutEffect(() => {
    if (!scrollContainer) {
      return undefined;
    }

    let frame: number | null = null;
    const update = (): void => {
      frame = null;
      const { scrollLeft } = scrollContainer;
      const nextTicks = getVisibleTicks(
        scrollLeft,
        Math.max(scrollContainer.clientWidth, 1),
        timelineStart,
        timelineEnd,
        contentWidth,
      );
      setTicks((current) =>
        hasSameTicks(current, nextTicks) ? current : nextTicks,
      );
    };
    const scheduleUpdate = (): void => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    scrollContainer.addEventListener('scroll', scheduleUpdate, {
      passive: true,
    });
    return (): void => {
      scrollContainer.removeEventListener('scroll', scheduleUpdate);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [contentWidth, scrollContainer, timelineEnd, timelineStart]);

  return ticks;
};

export const TimelineBackground: React.FC<TimelineBackgroundProps> = ({
  contentWidth,
  layout,
  visibleHours,
  scrollContainer,
  timelineEnd,
  timelineStart,
}) => {
  const { palette } = useTheme();
  const colors = palette.geowebColors;
  const selectionColor = colors.timeSlider.timelineSelectionBackground.fill!;
  const ticks = useVisibleTicks(
    scrollContainer,
    contentWidth,
    timelineStart,
    timelineEnd,
  );
  const weekly = visibleHours >= 336;
  const bandDuration = (weekly ? 7 : 1) * 86400000;

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'clip' }}>
      {getPeriodStarts(ticks, timelineStart, visibleHours).map((current) => {
        const left = getTimelineX(
          current,
          timelineStart,
          timelineEnd,
          contentWidth,
          0,
        );
        const right = getTimelineX(
          new Date(current.getTime() + bandDuration),
          timelineStart,
          timelineEnd,
          contentWidth,
          0,
        );
        const periodIndex = Math.floor(current.getTime() / bandDuration);
        const date = weekly
          ? `${formatPeriodDate(current)} - ${formatPeriodDate(new Date(current.getTime() + 6 * 86400000))}`
          : formatPeriodDate(current);
        // ISO weeks start on Monday; the Thursday determines the week year.
        const thursday = new Date(current.getTime() + 3 * 86400000);
        const week = Math.ceil(
          ((thursday.getTime() - Date.UTC(thursday.getUTCFullYear(), 0, 1)) /
            86400000 +
            1) /
            7,
        );
        const title = weekly
          ? `W${week}`
          : current.toLocaleDateString('en-GB', {
              timeZone: 'UTC',
              weekday: layout.weekday ?? 'long',
            });
        return (
          <Box
            data-testid="timeline-period-band"
            key={current.toISOString()}
            sx={{
              position: 'absolute',
              left,
              top: 0,
              bottom: 0,
              width: right - left,
              backgroundColor: colors.background.surface,
              backgroundImage:
                periodIndex % 2 === 0
                  ? `linear-gradient(to bottom, ${alpha(selectionColor, 0.5)} ${layout.headerHeight}px, ${alpha(selectionColor, 0.15)} ${layout.headerHeight}px)`
                  : 'none',
              // Clipping must not create a scroll container that traps sticky headings.
              overflow: 'clip',
            }}
          >
            <Box
              data-period-start={current.toISOString()}
              data-testid="timeline-period-label"
              sx={{
                fontSize: layout.fontSize,
                display: 'flex',
                flexDirection: layout.stackWeekLabels ? 'column' : 'row',
                alignItems: layout.stackWeekLabels ? 'flex-start' : 'center',
                columnGap: layout.weekday === 'long' ? '12px' : '8px',
                lineHeight: '20px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                padding: layout.stackWeekLabels ? '2px' : '2px 8px',
                height: layout.headerHeight,
                position: 'sticky',
                left: 0,
                width: 'max-content',
                maxWidth: '100%',
                color: colors.typographyAndIcons.text,
              }}
            >
              {weekly || layout.weekday ? (
                <Box
                  component="span"
                  sx={{
                    color: colors.warningsTimeline.headingText,
                    fontWeight: 500,
                  }}
                >
                  {title}
                </Box>
              ) : null}{' '}
              <Box component="span">{date}</Box>
            </Box>
          </Box>
        );
      })}
      {ticks.map((tick) => (
        <Box
          data-testid="timeline-tick-line"
          data-tick-type={tick.type}
          key={tick.time.toISOString()}
          sx={{
            position: 'absolute',
            left: tick.x,
            top: layout.headerHeight,
            bottom: 0,
            width: '1px',
            borderLeft: `1px solid ${tick.type === 'day' ? colors.warningsTimeline.dayBoundary : colors.warningsTimeline.grid}`,
          }}
        />
      ))}
    </Box>
  );
};

// A layer on top of the background with the block elements
export const WarningBlocksLayer = React.memo(
  ({
    warningBlocks,
    hoveredWarningId,
    onHoverWarning,
    onSelectWarning,
    selectedWarningId,
    warningListItemActions,
  }: WarningBlocksLayerProps) => {
    return (
      <>
        {warningBlocks.map((warningLayout) => (
          <WarningBlock
            highlighted={hoveredWarningId === warningLayout.warning.id}
            key={warningLayout.key}
            left={warningLayout.left}
            onHoverWarning={onHoverWarning}
            onSelectWarning={onSelectWarning}
            selected={selectedWarningId === warningLayout.warning.id}
            top={warningLayout.top}
            warning={warningLayout.warning}
            warningListItemActions={warningListItemActions}
            width={warningLayout.width}
          />
        ))}
      </>
    );
  },
);

// The time control at the bottom
export const TimelineTimeBar: React.FC<TimelineTimeBarProps> = ({
  contentWidth,
  layout,
  fullWidth,
  visibleHours,
  scrollContainer,
  timelineEnd,
  timelineStart,
}) => {
  const { palette } = useTheme();
  const colors = palette.geowebColors;
  const selectionColor = colors.timeSlider.timelineSelectionBackground.fill!;
  const ticks = useVisibleTicks(
    scrollContainer,
    contentWidth,
    timelineStart,
    timelineEnd,
  );

  return (
    <Box
      sx={{
        width: fullWidth,
        position: 'relative',
        height: '100%',
      }}
    >
      {getPeriodStarts(ticks, timelineStart, visibleHours).map((start) => {
        const duration = (visibleHours >= 336 ? 7 : 1) * 86400000;
        const left = getTimelineX(
          start,
          timelineStart,
          timelineEnd,
          contentWidth,
          0,
        );
        const right = getTimelineX(
          new Date(start.getTime() + duration),
          timelineStart,
          timelineEnd,
          contentWidth,
          0,
        );
        return (
          <Box
            key={start.toISOString()}
            sx={{
              position: 'absolute',
              left,
              width: right - left,
              top: 0,
              bottom: 0,
              backgroundColor:
                Math.floor(start.getTime() / duration) % 2 === 0
                  ? alpha(selectionColor, 0.5)
                  : colors.background.surface,
            }}
          />
        );
      })}
      {ticks.map((tick) => (
        <Box
          data-testid="timeline-axis-tick"
          key={`tick-${tick.time.toISOString()}`}
          sx={{
            position: 'absolute',
            left: tick.x,
            top: 0,
            height: tick.type === 'dashed' ? 4 : 7,
            borderLeft: `1px solid ${colors.warningsTimeline.dayBoundary}`,
          }}
        />
      ))}
      {ticks
        .filter((tick) => {
          const pixelsPerHour =
            contentWidth /
            ((timelineEnd.getTime() - timelineStart.getTime()) / 3600000);
          const intervals =
            visibleHours >= 336
              ? [24, 48, 72, 96, 168]
              : [3, 6, 12, 24, 48, 168];
          const labelHours =
            intervals.find(
              (hours) => hours * pixelsPerHour >= layout.axisLabelSpacing,
            ) ?? 168;
          return tick.time.getTime() % (labelHours * 3600000) === 0;
        })
        .map((tick) => {
          const label =
            visibleHours >= 336
              ? tick.time.toLocaleDateString('en-GB', {
                  timeZone: 'UTC',
                  day: '2-digit',
                  month: '2-digit',
                })
              : String(tick.time.getUTCHours()).padStart(2, '0');

          return (
            <Box
              data-testid="timeline-axis-label"
              key={`label-${tick.time.toISOString()}`}
              sx={{
                position: 'absolute',
                left: tick.x,
                top: 9,
                transform: 'translateX(-50%)',
                textAlign: 'center',
                lineHeight: 1.1,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: layout.fontSize,
                  color: colors.typographyAndIcons.text,
                }}
              >
                {label}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};
